import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { onGitStatusPush } from '@/websocket/wsClient'
import { useRepoStore } from '@/stores/repoStore'
import {
  getGitStatus,  getGitBranches, getGitLog,   getFileAtHead,
  stageFile,     unstageFile,    stageAll,    unstageAll,
  commitChanges, checkoutBranch, createBranch,
  pullChanges,   pushChanges,
  type GitStatusResult, type GitCommit,
} from '@/services/gitService'
import { readFile } from '@/services/fileService'

export interface DiffState {
  filePath: string
  original: string
  modified: string
  language: string
}

export const useGitStore = defineStore('git', () => {
  // ── State ───────────────────────────────────────────────────────────────────

  const status     = ref<GitStatusResult | null>(null)
  const branches   = ref<string[]>([])
  const currentBranch = computed(() => status.value?.current ?? branches.value[0] ?? '—')
  const commits    = ref<GitCommit[]>([])
  const diff       = ref<DiffState | null>(null)

  const loading    = ref(false)
  const busy       = ref(false)       // true while a mutating op is in-flight
  const busyLabel  = ref('')
  const error      = ref<string | null>(null)

  // The repo path used for all current git ops. Set by fetchStatus() callers.
  const repoPath   = ref<string | null>(null)

  // ── WebSocket: react to server-pushed status broadcasts ────────────────────
  // Registered once; the store lives for the app lifetime so no explicit cleanup needed.
  // The listener is held by the socket's event emitter — no local reference required.
  onGitStatusPush((payload) => {
    if (payload.repoPath && payload.repoPath !== repoPath.value) return
    applyRawStatus(payload.status)
  })

  // ── Internal helpers ────────────────────────────────────────────────────────

  function applyRawStatus(raw: unknown): void {
    if (!raw || typeof raw !== 'object') return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = raw as any
    const files = r.files ?? []
    status.value = {
      current:  r.current  ?? '',
      tracking: r.tracking ?? null,
      files,
      staged:   files.filter((f: { index: string }) =>
        f.index !== ' ' && f.index !== '?' && f.index !== ''),
      unstaged: files.filter((f: { working_dir: string }) =>
        f.working_dir !== ' ' && f.working_dir !== ''),
    }
  }

  function detectLanguage(path: string): string {
    const ext = path.split('.').pop() ?? ''
    const map: Record<string, string> = {
      twig: 'twig', php: 'php', js: 'javascript', ts: 'typescript',
      css: 'css', less: 'less', json: 'json', yaml: 'yaml',
      yml: 'yaml', html: 'html',
    }
    return map[ext] ?? 'plaintext'
  }

  // ── Read actions ────────────────────────────────────────────────────────────

  async function fetchStatus(path: string): Promise<void> {
    repoPath.value = path
    loading.value  = true
    error.value    = null
    try {
      status.value = await getGitStatus(path)
    } catch (err) {
      error.value = String(err)
    } finally {
      loading.value = false
    }
  }

  async function fetchBranches(path: string): Promise<void> {
    try {
      const summary  = await getGitBranches(path)
      branches.value = summary.all
    } catch {
      // non-fatal; branches list stays empty
    }
  }

  async function fetchLog(path: string, limit = 50): Promise<void> {
    try {
      const log      = await getGitLog(path, limit)
      commits.value  = log.all
    } catch {
      // non-fatal
    }
  }

  /**
   * Open the Monaco DiffEditor for a changed file.
   *
   * @param absRepoPath  - Absolute filesystem path to the repo (for git socket)
   * @param relFilePath  - Path relative to the repo root (from git status)
   * @param wsFilePath   - Workspace-relative path for the PHP file API read endpoint
   */
  async function openDiff(absRepoPath: string, relFilePath: string, wsFilePath: string): Promise<void> {
    error.value = null
    try {
      const [original, modified] = await Promise.all([
        getFileAtHead(absRepoPath, relFilePath),
        readFile(wsFilePath).catch(() => ''),  // empty for deleted files
      ])
      diff.value = {
        filePath: relFilePath,
        original,
        modified,
        language: detectLanguage(relFilePath),
      }
    } catch (err) {
      error.value = String(err)
    }
  }

  function closeDiff(): void {
    diff.value = null
  }

  // ── Mutating actions ────────────────────────────────────────────────────────
  // Each action sets busy + busyLabel so the UI can show a spinner/label,
  // then refreshes status after success (server broadcast covers the WS case;
  // the explicit refresh is a safety net in case the broadcast races the ack).

  async function stage(filePath: string): Promise<void> {
    if (!repoPath.value) return
    await run('Staging…', () => stageFile(repoPath.value!, filePath))
  }

  async function unstage(filePath: string): Promise<void> {
    if (!repoPath.value) return
    await run('Unstaging…', () => unstageFile(repoPath.value!, filePath))
  }

  async function stageAllFiles(): Promise<void> {
    if (!repoPath.value) return
    await run('Staging all…', () => stageAll(repoPath.value!))
  }

  async function unstageAllFiles(): Promise<void> {
    if (!repoPath.value) return
    await run('Unstaging all…', () => unstageAll(repoPath.value!))
  }

  async function commit(message: string): Promise<void> {
    if (!repoPath.value) return
    const repoStore = useRepoStore()
    await run('Committing…', () => commitChanges(repoPath.value!, message, repoStore.currentUser, repoStore.currentUserEmail))
  }

  async function checkout(branch: string): Promise<void> {
    if (!repoPath.value) return
    await run(`Checking out ${branch}…`, async () => {
      await checkoutBranch(repoPath.value!, branch)
      await fetchBranches(repoPath.value!)
    })
  }

  async function createNewBranch(name: string): Promise<void> {
    if (!repoPath.value) return
    await run(`Creating branch ${name}…`, async () => {
      await createBranch(repoPath.value!, name)
      await fetchBranches(repoPath.value!)
    })
  }

  async function pull(): Promise<void> {
    if (!repoPath.value) return
    const nasPassword = await resolveNasPassword(repoPath.value)
    await run('Pulling…', () => pullChanges(repoPath.value!, nasPassword))
  }

  async function push(): Promise<void> {
    if (!repoPath.value) return
    const nasPassword = await resolveNasPassword(repoPath.value)
    await run('Pushing…', () => pushChanges(repoPath.value!, nasPassword))
  }

  async function resolveNasPassword(path: string): Promise<string | undefined> {
    const repoStore = useRepoStore()
    const userRepo  = repoStore.userRepos.find(r => r.localPath === path)
    if (!userRepo) return undefined
    const isSsh = userRepo.nasUrl.startsWith('ssh://') || userRepo.nasUrl.startsWith('git@')
    if (!isSsh) return undefined
    try {
      return await repoStore.getNasPassword(userRepo.id)
    } catch {
      return undefined
    }
  }

  // Wraps any mutating op: sets busy/label, catches errors, refreshes status.
  async function run(label: string, fn: () => Promise<void>): Promise<void> {
    busy.value      = true
    busyLabel.value = label
    error.value     = null
    try {
      await fn()
      if (repoPath.value) await fetchStatus(repoPath.value)
    } catch (err) {
      error.value = String(err)
    } finally {
      busy.value      = false
      busyLabel.value = ''
    }
  }

  return {
    status,
    branches,
    currentBranch,
    commits,
    diff,
    loading,
    busy,
    busyLabel,
    error,
    repoPath,
    // actions
    fetchStatus,
    fetchBranches,
    fetchLog,
    openDiff,
    closeDiff,
    stage,
    unstage,
    stageAllFiles,
    unstageAllFiles,
    commit,
    checkout,
    createNewBranch,
    pull,
    push,
  }
})
